import { apiGet, API_BASE } from "../services/api";

const REGISTRY_TIMEOUT_MS = 8000;

/** Race a promise against a timeout so we never hang on "Loading procedures". */
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error("Procedure registry request timed out")), ms)
    ),
  ]);
}

export interface ProcedureEntry {
  id?: string;
  master_id?: string;
  name?: string;
  display_name?: string;
  category?: string;
  target_selector?: string;
  /** From procedure_master.requires_tooth: procedure needs tooth selection to be active */
  requires_tooth?: boolean;
  default_steps?: (string | { label?: string; chart_mutation?: string })[];
  chart_mutation?: string;
  estimated_cost_inr?: number;
  cost?: number;
  standard_code?: string;
  [key: string]: unknown;
}

let PROCEDURE_REGISTRY: Record<string, ProcedureEntry> = {};
let REGISTRY_LOADED = false;
let REGISTRY_LOADING: Promise<Record<string, ProcedureEntry>> | null = null;

/** Normalize API/JSON registry so each entry has master_id and is keyed by master_id. */
function normalizeRegistry(raw: Record<string, ProcedureEntry>): Record<string, ProcedureEntry> {
  const out: Record<string, ProcedureEntry> = {};
  for (const [key, entry] of Object.entries(raw)) {
    if (!entry || typeof entry !== "object") continue;
    const masterId = (entry.master_id ?? key) as string;
    if (!masterId) continue;
    out[masterId] = { ...entry, master_id: masterId };
  }
  return out;
}

export async function loadProcedureRegistry(forceReload = false): Promise<Record<string, ProcedureEntry>> {
  if (REGISTRY_LOADED && !forceReload) {
    return PROCEDURE_REGISTRY;
  }

  if (REGISTRY_LOADING) {
    return REGISTRY_LOADING;
  }

  REGISTRY_LOADING = new Promise(async (resolve, reject) => {
    const setAndResolve = (data: Record<string, ProcedureEntry>) => {
      PROCEDURE_REGISTRY = data;
      REGISTRY_LOADED = true;
      REGISTRY_LOADING = null;
      if (Object.keys(data).length > 0) {
        if (process.env.NODE_ENV === "development") {
          console.debug("Loaded procedure registry items", Object.keys(data).length);
        }
      }
      resolve(PROCEDURE_REGISTRY);
    };

    try {
      // 1) API registry with timeout so we never hang on "Loading procedures"
      try {
        const data = await withTimeout(
          apiGet("/api/procedures/registry") as Promise<Record<string, ProcedureEntry> | null>,
          REGISTRY_TIMEOUT_MS
        );
        if (data && typeof data === "object" && Object.keys(data).length > 0) {
          setAndResolve(normalizeRegistry(data));
          return;
        }
      } catch (_) {
        // continue to fallbacks
      }

      // 2) Static JSON with timeout – try API_BASE then same-origin
      for (const baseUrl of [API_BASE, ""]) {
        try {
          const url = baseUrl ? `${String(baseUrl).replace(/\/$/, "")}/frontend_registry_procedures.json` : "/frontend_registry_procedures.json";
          const res = await withTimeout(fetch(url), REGISTRY_TIMEOUT_MS);
          if (res.ok) {
            const jsonData = await res.json();
            if (jsonData && typeof jsonData === "object" && Object.keys(jsonData).length > 0) {
              setAndResolve(normalizeRegistry(jsonData));
              return;
            }
          }
        } catch (_) {
          // next fallback
        }
      }

      // 3) Doctor procedure-registry (FastAPI /api/doctor/procedure-registry)
      try {
        const data = await withTimeout(
          apiGet("/api/doctor/procedure-registry") as Promise<Record<string, ProcedureEntry> | null>,
          REGISTRY_TIMEOUT_MS
        );
        if (data && typeof data === "object" && Object.keys(data).length > 0) {
          setAndResolve(normalizeRegistry(data));
          return;
        }
      } catch (_) {
        // fall through
      }

      // 4) Manager billable procedures (Array format fallback)
      try {
        const data = await withTimeout(
          apiGet("/api/manager/billable-procedures"),
          REGISTRY_TIMEOUT_MS
        );
        if (Array.isArray(data) && data.length > 0) {
          const dict: Record<string, ProcedureEntry> = {};
          data.forEach((p: any) => {
             const key = p.master_id || p.standard_code || String(p.id);
             dict[key] = {
                ...p,
                master_id: key,
                display_name: p.name,
                estimated_cost_inr: Number(p.price || p.cost || p.default_fee || 0),
             };
          });
          setAndResolve(dict);
          return;
        }
      } catch (_) {
        // fall through
      }

      // No source had data – resolve with empty so UI can show error; do not reject
      console.warn("Procedure registry: no procedures found from API, static JSON, or procedure_master.");
      setAndResolve({});
    } catch (e) {
      REGISTRY_LOADING = null;
      console.error("❌ Failed to load procedure registry", e);
      reject(e);
    }
  });

  return REGISTRY_LOADING;
}

export function getProcedureRegistry(): Record<string, ProcedureEntry> {
  return PROCEDURE_REGISTRY;
}

export function getProcedureByMasterId(masterId: string | null | undefined): ProcedureEntry | null {
  if (!masterId) return null;
  const byKey = PROCEDURE_REGISTRY[masterId];
  if (byKey) return byKey;
  const normalized = String(masterId).trim().toLowerCase();
  if (!normalized) return null;
  const normalizedNoSpaces = normalized.replace(/\s+/g, "");
  const byDisplayName = Object.values(PROCEDURE_REGISTRY).find((p) => {
    const display = (p.display_name || p.name || "").toString().toLowerCase();
    const key = (p.master_id || "").toString().toLowerCase();
    return (
      display === normalized ||
      key === normalized ||
      display.replace(/\s+/g, "") === normalizedNoSpaces ||
      key.replace(/\s+/g, "") === normalizedNoSpaces
    );
  });
  return byDisplayName || null;
}

/** Normalize for flexible matching: lowercase, replace punctuation/dashes with space, collapse spaces. */
function normalizeForMatch(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[-–—,_./]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Find master_id by procedure type / display name (for resume when session only has procedure_type). */
export function getMasterIdByProcedureType(procedureType: string | null | undefined): string | null {
  if (!procedureType || typeof procedureType !== "string") return null;
  const normalized = normalizeForMatch(procedureType);
  if (!normalized) return null;
  const entry = Object.values(PROCEDURE_REGISTRY).find((p) => {
    const display = (p.display_name || p.name || "").toString();
    const key = (p.master_id || "").toString();
    return (
      normalizeForMatch(display) === normalized ||
      normalizeForMatch(key) === normalized ||
      normalizeForMatch(display).replace(/\s/g, "") === normalized.replace(/\s/g, "")
    );
  });
  return entry ? ((entry.master_id as string) ?? null) : null;
}

export function getProcedureByDbId(dbId: string | number | null | undefined): ProcedureEntry | null {
  if (dbId == null) return null;
  const values = Object.values(PROCEDURE_REGISTRY);
  return values.find(p => p.id === dbId) || null;
}

export function getProceduresByCategory(category: string | null | undefined): ProcedureEntry[] {
  if (!category) return [];
  return Object.values(PROCEDURE_REGISTRY).filter(
    p => String(p.category || "").toLowerCase() === String(category).toLowerCase()
  );
}

export function searchProcedures(query: string | null | undefined): ProcedureEntry[] {
  if (!query) return Object.values(PROCEDURE_REGISTRY);
  const q = query.toLowerCase();
  return Object.values(PROCEDURE_REGISTRY).filter(p =>
    (p.display_name || p.name || "").toLowerCase().includes(q) ||
    (p.master_id || "").toLowerCase().includes(q) ||
    (p.standard_code || "").toLowerCase().includes(q)
  );
}

export function getProcedureSteps(masterId: string | null | undefined): (string | { label?: string; chart_mutation?: string })[] {
  const proc = getProcedureByMasterId(masterId ?? undefined);
  return (proc?.default_steps || []) as (string | { label?: string; chart_mutation?: string })[];
}

export function getStepAt(masterId: string | null | undefined, stepNo: number): string | { label?: string; chart_mutation?: string } | null {
  const steps = getProcedureSteps(masterId);
  return steps[stepNo - 1] ?? null;
}

export function getTotalSteps(masterId: string | null | undefined): number {
  return getProcedureSteps(masterId).length;
}

export function getStepMutation(masterId: string | null | undefined, stepNo: number): string {
  const step = getStepAt(masterId, stepNo);
  if (typeof step === "string") return "NONE";
  return (step as { chart_mutation?: string })?.chart_mutation || "NONE";
}

export function getTargetSelector(masterId: string | null | undefined): string {
  const proc = getProcedureByMasterId(masterId ?? undefined);
  return proc?.target_selector || "none";
}

export function getProcedureCost(masterId: string | null | undefined): number {
  const proc = getProcedureByMasterId(masterId ?? undefined);
  return (proc?.estimated_cost_inr ?? proc?.cost ?? 0) as number;
}

export function getProcedureStandardCode(masterId: string | null | undefined): string | null {
  const proc = getProcedureByMasterId(masterId ?? undefined);
  return proc?.standard_code ?? null;
}

export function getProcedureFinalMutation(masterId: string | null | undefined): string {
  const proc = getProcedureByMasterId(masterId ?? undefined);
  return (proc?.chart_mutation as string) || "NONE";
}

export function getProcedureCategories(): string[] {
  const cats = new Set<string>();
  Object.values(PROCEDURE_REGISTRY).forEach(p => {
    if (p.category) cats.add(p.category);
  });
  return Array.from(cats).sort();
}

export function getGroupedProcedures(): Record<string, ProcedureEntry[]> {
  const grouped: Record<string, ProcedureEntry[]> = {};
  Object.values(PROCEDURE_REGISTRY).forEach(p => {
    const cat = p.category || "Other";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(p);
  });
  Object.keys(grouped).forEach(cat => {
    grouped[cat] = grouped[cat].sort((a, b) =>
      (a.display_name || a.name || "").localeCompare(b.display_name || b.name || "")
    );
  });
  return grouped;
}

export async function reloadProcedureRegistry(): Promise<Record<string, ProcedureEntry>> {
  REGISTRY_LOADED = false;
  return loadProcedureRegistry(true);
}

export async function getProcedure(masterId: string | null | undefined): Promise<ProcedureEntry | null> {
  if (!REGISTRY_LOADED) {
    await loadProcedureRegistry();
  }
  return getProcedureByMasterId(masterId ?? undefined);
}

export function getProcedureSync(masterId: string | null | undefined): ProcedureEntry | null {
  if (!REGISTRY_LOADED) {
    console.warn("Procedure registry not loaded. Call loadProcedureRegistry() first.");
    return null;
  }
  return getProcedureByMasterId(masterId ?? undefined);
}

export async function initProcedureRegistry(): Promise<void> {
  await loadProcedureRegistry();
}

export function getAllProcedures(): ProcedureEntry[] {
  return Object.values(PROCEDURE_REGISTRY);
}
